import React, { useEffect, useState } from 'react';
import { generatePath, Navigate, useParams } from 'react-router-dom';
import { NODON_SHARED_PROJECT_PATH } from '@shared/constants/router.constants';
import { NODON_NOT_FOUND } from '@shared/constants';
import { useProjectState } from '@client/store/project';
import { GlobalLoader } from '@ui/GlobalLoader';
import { Page } from '@client/store/ui/ui.model';

/**
 * Renders when user opens a short shared link (/shared/:sharingKey).
 * Fetches the project by sharing key and redirects to the full URL with version.
 */
const SharedProjectRedirect: React.FC = () => {
  const { sharingKey } = useParams<{ sharingKey: string }>();
  const { fetchPublicProject } = useProjectState('fetchPublicProject');
  const [targetPath, setTargetPath] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!sharingKey) {
      setTargetPath(NODON_NOT_FOUND);
      return;
    }
    let cancelled = false;
    fetchPublicProject(sharingKey)
      .then((project) => {
        if (cancelled || !project) {
          if (!project) setTargetPath(NODON_NOT_FOUND);
          return;
        }
        const versionId = project.active_version_id;
        setTargetPath(
          generatePath(NODON_SHARED_PROJECT_PATH, {
            sharingKey,
            versionId,
            page: Page.Design,
          }),
        );
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [sharingKey, fetchPublicProject]);

  if (error || targetPath === NODON_NOT_FOUND) {
    return <Navigate to={NODON_NOT_FOUND} replace />;
  }
  if (targetPath) {
    return <Navigate to={targetPath} replace />;
  }
  return <GlobalLoader.Register text="Loading project" />;
};

export default SharedProjectRedirect;
